import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router';
import { Menu, Phone, X } from 'lucide-react';
import { CallBar } from '@/components/CallBar';
import { NAV_SECTIONS } from '@/data/homeContent';
import { useContact } from '@/features/settings/useSiteSettings';

const LEGAL_LINKS = [
  { to: '/aszf', label: 'Általános Szerződési Feltételek' },
  { to: '/adatvedelem', label: 'Adatkezelési tájékoztató' },
  { to: '/cookie', label: 'Cookie-tájékoztató' }
];

export const PublicLayout = () => {
  const contact = useContact();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [menuOpen]);

  return (
    <div className="flex min-h-screen flex-col pb-14 sm:pb-0">
      <a href="#tartalom" className="skip-link">
        Ugrás a tartalomra
      </a>

      <header className="sticky top-0 z-40 border-b border-line bg-cream/95 backdrop-blur">
        <div className="mx-auto flex min-h-[74px] w-full max-w-[1220px] items-center gap-3 px-4 sm:gap-5 sm:px-6">
          <Link to="/" className="mr-auto flex min-h-12 items-center gap-3 text-navy no-underline">
            <span className="font-display text-base leading-tight font-bold sm:text-lg">
              Betegágy Bérlés
              <span className="mt-0.5 hidden font-sans text-xs font-normal text-ink-soft sm:block">
                {contact.serviceArea}
              </span>
            </span>
          </Link>

          <nav aria-label="Fő navigáció" className="hidden lg:flex lg:gap-6">
            <Link
              to="/termekek"
              className="flex min-h-11 items-center border-b-2 border-transparent font-semibold text-navy no-underline hover:border-teal"
            >
              Termékek
            </Link>
            {NAV_SECTIONS.map((item) => (
              <a
                key={item.href}
                href={`/${item.href}`}
                className="flex min-h-11 items-center border-b-2 border-transparent font-semibold text-navy no-underline hover:border-teal"
              >
                {item.label}
              </a>
            ))}
          </nav>

          <a
            href={contact.phoneHref}
            className="hidden min-h-12 shrink-0 items-center gap-2.5 rounded-md px-4 font-display text-lg font-bold whitespace-nowrap text-navy no-underline hover:bg-sand sm:flex"
          >
            <Phone className="size-5 shrink-0 text-teal-ink" aria-hidden="true" />
            {contact.phone}
          </a>

          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            aria-expanded={menuOpen}
            aria-controls="mobil-menu"
            aria-label={menuOpen ? 'Menü bezárása' : 'Menü megnyitása'}
            className="flex size-12 shrink-0 items-center justify-center rounded-md border-2 border-navy text-navy lg:hidden"
          >
            {menuOpen ? <X className="size-6" aria-hidden="true" /> : <Menu className="size-6" aria-hidden="true" />}
          </button>
        </div>

        {menuOpen && (
          <nav id="mobil-menu" aria-label="Fő navigáció (mobil)" className="border-t border-line bg-cream lg:hidden">
            <ul className="mx-auto flex w-full max-w-[1220px] list-none flex-col p-2">
              <li>
                <Link
                  to="/termekek"
                  onClick={() => setMenuOpen(false)}
                  className="flex min-h-14 items-center rounded-md px-4 font-semibold text-navy no-underline hover:bg-sand"
                >
                  Termékek
                </Link>
              </li>
              {NAV_SECTIONS.map((item) => (
                <li key={item.href}>
                  <a
                    href={`/${item.href}`}
                    onClick={() => setMenuOpen(false)}
                    className="flex min-h-14 items-center rounded-md px-4 font-semibold text-navy no-underline hover:bg-sand"
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        )}
      </header>

      <main id="tartalom" className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t border-line bg-navy text-cream">
        <div className="mx-auto w-full max-w-[1220px] px-4 py-12 sm:px-6">
          <div className="grid gap-8 md:grid-cols-3">
            <div>
              <h2 className="font-display text-lg font-bold text-cream">Ápolási betegágy bérlés</h2>
              <p className="mt-2 text-sm text-cream/75">
                Elektromos betegágyak, matracok és betegápolási segédeszközök bérlése{' '}
                {contact.serviceArea}ben.
              </p>
            </div>

            <div>
              <h2 className="font-display text-lg font-bold text-cream">Kapcsolat</h2>
              <ul className="mt-2 flex list-none flex-col gap-1 p-0 text-sm">
                <li>
                  <a href={contact.phoneHref} className="inline-flex min-h-11 items-center text-cream underline-offset-4">
                    {contact.phone}
                  </a>
                </li>
                <li>
                  <a href={`mailto:${contact.email}`} className="inline-flex min-h-11 items-center text-cream underline-offset-4">
                    {contact.email}
                  </a>
                </li>
                <li className="py-2 text-cream/75">{contact.weekdayHours}</li>
              </ul>
            </div>

            <div>
              <h2 className="font-display text-lg font-bold text-cream">Dokumentumok</h2>
              <ul className="mt-2 flex list-none flex-col gap-1 p-0 text-sm">
                {LEGAL_LINKS.map((link) => (
                  <li key={link.to}>
                    <Link to={link.to} className="inline-flex min-h-11 items-center text-cream underline-offset-4">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <p className="mt-10 text-sm text-cream/60">
            © {new Date().getFullYear()} Ápolási betegágy bérlés. Minden jog fenntartva.
          </p>
        </div>
      </footer>

      <CallBar />
    </div>
  );
};
